import {
  ArrowForwardRounded,
  AutoAwesomeRounded,
  CheckCircleRounded,
  MailOutlineRounded,
  OpenInNewRounded,
  PauseRounded,
  PlayArrowRounded,
  PublicRounded,
  SendRounded,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  CssBaseline,
  IconButton,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import './landing.css';

const problems = [
  {
    title: '返信に追われて、大会準備が進まない',
    description:
      'エントリー開始前や開催直前には、同じような質問が何通も届きます。一通ずつ調べて返信する時間が、本来の運営業務を圧迫します。',
  },
  {
    title: '早く返したい。でも、間違えられない',
    description: '大会情報は更新されます。正確さと丁寧さを保ちながら、繁忙期でも参加者を待たせない対応が求められます。',
  },
];

const solutions = [
  {
    icon: <AutoAwesomeRounded />,
    title: 'AIが返信を下書き',
    description: '問い合わせ内容を読み取り、設定したナレッジとトーンをもとに返信案を作成します。',
  },
  {
    icon: <PublicRounded />,
    title: '公式情報を参照',
    description: '大会サイトやFAQの情報を参照し、更新される案内にも対応した下書きを用意します。',
  },
  {
    icon: <CheckCircleRounded />,
    title: '人が確認して送信',
    description: '下書きは必ず担当者が確認・編集してから送信。送信後30秒間は取り消しもできます。',
  },
];

function GradientButton({ children, sx, ...props }) {
  return (
    <Button
      variant="contained"
      sx={{
        minHeight: 46,
        px: 3,
        borderRadius: 2,
        background: 'var(--landing-gradient)',
        boxShadow: 'none',
        '&:hover': { background: 'var(--landing-gradient)', boxShadow: 'none' },
        '&:focus-visible': { outline: '3px solid #25252a', outlineOffset: 3 },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

export function LandingPage({ brandTheme, brandLogo, productName, audienceLabel, preview }) {
  const heroVideoRef = useRef(null);
  const [isHeroVideoPaused, setIsHeroVideoPaused] = useState(false);

  const handleHeroVideoToggle = () => {
    const heroVideo = heroVideoRef.current;
    if (!heroVideo) {
      return;
    }

    if (heroVideo.paused) {
      heroVideo.play().catch(() => setIsHeroVideoPaused(true));
    } else {
      heroVideo.pause();
    }
  };

  return (
    <ThemeProvider theme={brandTheme}>
      <CssBaseline />
      <a className="skip-link" href="#main-content">
        本文へ移動
      </a>
      <Box component="main" className="landing-page" sx={{ '--landing-gradient': brandTheme.brand.gradient }}>
        <Box component="header" className="landing-header">
          <Container maxWidth="lg" className="landing-header-inner">
            <Box className="brand-lockup" aria-label={productName}>
              <Box component="img" src={brandLogo} alt="" className="brand-mark" width="34" height="34" />
              <Typography component="span" className="brand-name">
                RunPort <strong>Desk</strong>
              </Typography>
            </Box>
            <GradientButton component="a" href="#contact">
              導入のご相談
            </GradientButton>
          </Container>
        </Box>

        <Box component="section" id="main-content" tabIndex={-1} className="hero-section">
          <Box className="hero-video-layer" aria-hidden="true">
            <Box
              component="video"
              ref={heroVideoRef}
              className="hero-video"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              onPlay={() => setIsHeroVideoPaused(false)}
              onPause={() => setIsHeroVideoPaused(true)}
            >
              <source src="/assets/videos/desk-hero.webm" type="video/webm" />
            </Box>
            <Box className="hero-video-overlay" />
          </Box>
          <IconButton
            className="hero-video-control"
            onClick={handleHeroVideoToggle}
            aria-label={isHeroVideoPaused ? '背景動画を再生' : '背景動画を一時停止'}
          >
            {isHeroVideoPaused ? <PlayArrowRounded /> : <PauseRounded />}
          </IconButton>
          <Container maxWidth="lg" className="hero-container">
            <Box className="hero-copy">
              <Typography className="hero-overline">RUNPORT DESK</Typography>
              <Typography component="h1" className="hero-title">
                大会の問い合わせ対応を、
                <br />
                もっと軽やかに。
              </Typography>
              <Typography className="hero-description">
                {audienceLabel}のメール対応を支えるAIアシスタント。
                <br />
                返信の下書きを用意し、あなたは確認して送るだけです。
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="center"
                className="hero-actions"
              >
                <Button
                  component="a"
                  href="#contact"
                  variant="contained"
                  className="hero-contact-button"
                  endIcon={<ArrowForwardRounded />}
                >
                  導入のご相談
                </Button>
                <Button component="a" href="/login" variant="outlined" className="hero-login-button">
                  ログイン
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>

        <Box component="section" className="problem-section">
          <Container maxWidth="lg">
            <Box className="section-heading">
              <Typography component="h2">大会運営の、こんな悩みに。</Typography>
              <Typography>参加者への対応を大切にしたいからこそ、問い合わせ業務の負担を減らします。</Typography>
            </Box>
            <Box className="problem-grid">
              {problems.map((problem) => (
                <Card key={problem.title} className="problem-card">
                  <CardContent>
                    <Typography component="h3">{problem.title}</Typography>
                    <Typography>{problem.description}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Container>
        </Box>

        <Box component="section" className="solution-section">
          <Container maxWidth="lg">
            <Box className="section-heading">
              <Typography component="h2">RunPort Deskの解決策</Typography>
              <Typography>AIが「書く」を支え、人が「判断」と「心配り」に集中できる仕組みです。</Typography>
            </Box>
            <Box className="solution-grid">
              {solutions.map((solution) => (
                <Card key={solution.title} className="solution-card">
                  <CardContent>
                    <Box className="solution-icon">{solution.icon}</Box>
                    <Typography component="h3">{solution.title}</Typography>
                    <Typography>{solution.description}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Container>
        </Box>

        <Box component="section" className="screen-section">
          <Container maxWidth="lg">
            <Box className="section-heading">
              <Typography component="h2">実際の画面</Typography>
              <Typography>届いたメールとAIの返信案を、ひとつの画面で確認できます。</Typography>
            </Box>
            <Box className="desk-preview" aria-label={`${productName}の問い合わせ返信下書き画面のイメージ`}>
              <Box className="preview-topbar">
                <Box className="preview-dots">
                  <i />
                  <i />
                  <i />
                </Box>
                <Typography>受信トレイ</Typography>
                <Box component="span">未対応 12</Box>
              </Box>
              <Box className="preview-content">
                <Box className="inbox-list">
                  <Typography>新着の問い合わせ</Typography>
                  {preview.contacts.map((contact, index) => (
                    <Box key={contact.subject} className={`inbox-item ${index === 0 ? 'active' : ''}`}>
                      <Box className="avatar">{contact.initial}</Box>
                      <Box>
                        <strong>{contact.subject}</strong>
                        <span>{index === 0 ? '本日 10:42' : index === 1 ? '本日 10:18' : '昨日'}</span>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box className="reply-panel">
                  <Box className="mail-subject">
                    <MailOutlineRounded />
                    <Typography>{preview.contacts[0].subject}</Typography>
                  </Box>
                  <Typography className="mail-from">{preview.sender} 様からの問い合わせ</Typography>
                  <Box className="ai-draft">
                    <Typography className="draft-label">
                      <AutoAwesomeRounded />
                      AIが下書きを作成しました
                    </Typography>
                    <Typography>
                      {preview.sender}様<br />
                      <br />
                      {preview.draft}
                      <br />
                      <br />
                      {preview.signature}
                    </Typography>
                  </Box>
                  <Box className="preview-footer">
                    <Typography>内容を確認・編集できます</Typography>
                    <Box component="span">
                      <SendRounded />
                      確認して送信
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>

        <Box component="section" className="flow-section">
          <Container maxWidth="lg">
            <Box className="section-heading">
              <Typography component="h2">導入の流れ</Typography>
              <Typography>大会情報とメール受信設定を整え、利用を開始します。</Typography>
            </Box>
            <Box className="flow-grid">
              <Box>
                <strong>1</strong>
                <Typography component="h3">ご相談</Typography>
                <Typography>運営体制や課題をお聞かせください。</Typography>
              </Box>
              <Box>
                <strong>2</strong>
                <Typography component="h3">大会情報を設定</Typography>
                <Typography>公式サイトやFAQなど、返信に必要な情報を登録します。</Typography>
              </Box>
              <Box>
                <strong>3</strong>
                <Typography component="h3">利用開始</Typography>
                <Typography>AIの下書きを確認しながら、メール対応を進めます。</Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        <Box component="section" className="runport-section">
          <Container maxWidth="md">
            <Typography component="h2">RunPortについて</Typography>
            <Typography>
              <a href="https://runport.run" target="_blank" rel="noreferrer">
                RunPort
              </a>
              は、マラソン大会の主催者とランナー、双方により良い体験を届けるサービスブランドです。大会運営の「当たり前」を見直し、もっと素晴らしい大会づくりを支えます。
            </Typography>
            <Button
              component="a"
              href="https://runport.run"
              target="_blank"
              rel="noreferrer"
              endIcon={<OpenInNewRounded />}
            >
              RunPort ブランドサイト
            </Button>
          </Container>
        </Box>

        <Box component="section" id="contact" className="contact-section">
          <Container maxWidth="md">
            <Card className="contact-card">
              <CardContent>
                <Typography component="h2">導入のご相談</Typography>
                <Typography>RunPort Deskの導入について、お気軽にご相談ください。</Typography>
                <GradientButton
                  component="a"
                  href="mailto:contact@swandive.co.jp?subject=RunPort%20Desk%E3%81%AE%E5%B0%8E%E5%85%A5%E7%9B%B8%E8%AB%87"
                  endIcon={<MailOutlineRounded />}
                >
                  メールで相談する
                </GradientButton>
              </CardContent>
            </Card>
          </Container>
        </Box>

        <Box component="footer" className="landing-footer">
          <Container maxWidth="lg">
            <Typography>
              © {new Date().getFullYear()}{' '}
              <a href="https://swandive.co.jp" target="_blank" rel="noreferrer">
                株式会社Swandive
              </a>
            </Typography>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
